import React, {useState, useEffect} from 'react';
import {useAuth} from '../contexts/AuthContext';
import {useNavigate} from 'react-router-dom';
import axios from 'axios';

const AdminInvitations = () => {
    const [invitations, setInvitations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        notes: '',
        expiresAt: '',
    });
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 0,
    });

    const {user, token} = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!user?.isAdmin) {
            navigate('/dashboard');
            return;
        }
        fetchInvitations();
    }, [user, navigate, pagination.page]);

    const fetchInvitations = async () => {
        try {
            const response = await axios.get(`/api/invitations?page=${pagination.page}&limit=${pagination.limit}`, {
                headers: {Authorization: `Bearer ${token}`},
            });
            setInvitations(response.data.invitations);
            setPagination(response.data.pagination);
        } catch (error) {
            setError('Failed to fetch invitations');
            console.error('Error fetching invitations:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateInvitation = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                email: formData.email,
                notes: formData.notes || undefined,
                expiresAt: formData.expiresAt || undefined,
            };

            await axios.post(`/api/invitations`, payload, {headers: {Authorization: `Bearer ${token}`}});

            setFormData({email: '', notes: '', expiresAt: ''});
            setShowCreateForm(false);
            fetchInvitations();
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to create invitation');
        }
    };

    const handleResendInvitation = async (invitationId) => {
        try {
            await axios.post(`/api/invitations/${invitationId}/resend`, {}, {headers: {Authorization: `Bearer ${token}`}});
            alert('Invitation resent successfully');
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to resend invitation');
        }
    };

    const handleDeleteInvitation = async (invitationId) => {
        if (!window.confirm('Are you sure you want to delete this invitation?')) {
            return;
        }

        try {
            await axios.delete(`/api/invitations/${invitationId}`, {headers: {Authorization: `Bearer ${token}`}});
            fetchInvitations();
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to delete invitation');
        }
    };

    const copyInviteLink = (token) => {
        const inviteUrl = `${window.location.origin}/register?token=${token}`;
        navigator.clipboard.writeText(inviteUrl);
        alert('Invite link copied to clipboard');
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    if (!user?.isAdmin) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-900">Access Denied</h1>
                    <p className="mt-2 text-gray-600">Admin access required</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Manage Invitations</h1>
                    <p className="mt-2 text-gray-600">Create and manage artist invitations</p>
                </div>

                {error && (
                    <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path
                                        fillRule="evenodd"
                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-700">{error}</p>
                            </div>
                            <div className="ml-auto pl-3">
                                <button onClick={() => setError('')} className="inline-flex text-red-400 hover:text-red-600">
                                    <span className="sr-only">Dismiss</span>
                                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path
                                            fillRule="evenodd"
                                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Create Invitation Button */}
                <div className="mb-6">
                    <button
                        onClick={() => setShowCreateForm(!showCreateForm)}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Create Invitation
                    </button>
                </div>

                {/* Create Invitation Form */}
                {showCreateForm && (
                    <div className="mb-6 bg-white shadow rounded-lg p-6">
                        <h2 className="text-lg font-medium text-gray-900 mb-4">Create New Invitation</h2>
                        <form onSubmit={handleCreateInvitation} className="space-y-4">
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                    Email Address
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                                    required
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            <div>
                                <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                                    Notes (Optional)
                                </label>
                                <textarea
                                    id="notes"
                                    value={formData.notes}
                                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                                    rows={3}
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            <div>
                                <label htmlFor="expiresAt" className="block text-sm font-medium text-gray-700">
                                    Expires At (Optional)
                                </label>
                                <input
                                    type="datetime-local"
                                    id="expiresAt"
                                    value={formData.expiresAt}
                                    onChange={(e) => setFormData({...formData, expiresAt: e.target.value})}
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            <div className="flex space-x-3">
                                <button
                                    type="submit"
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Create Invitation
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowCreateForm(false)}
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Invitations List */}
                <div className="bg-white shadow rounded-lg">
                    <div className="px-4 py-5 sm:p-6">
                        <h2 className="text-lg font-medium text-gray-900 mb-4">Invitations</h2>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expires</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {invitations.map((invitation) => (
                                        <tr key={invitation.id}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">{invitation.email}</div>
                                                {invitation.notes && <div className="text-sm text-gray-500">{invitation.notes}</div>}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span
                                                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                        invitation.isUsed
                                                            ? 'bg-green-100 text-green-800'
                                                            : invitation.expiresAt && new Date(invitation.expiresAt) < new Date()
                                                            ? 'bg-red-100 text-red-800'
                                                            : 'bg-yellow-100 text-yellow-800'
                                                    }`}
                                                >
                                                    {invitation.isUsed
                                                        ? 'Used'
                                                        : invitation.expiresAt && new Date(invitation.expiresAt) < new Date()
                                                        ? 'Expired'
                                                        : 'Pending'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(invitation.createdAt)}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {invitation.expiresAt ? formatDate(invitation.expiresAt) : 'Never'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                                <button
                                                    onClick={() => copyInviteLink(invitation.token)}
                                                    className="text-blue-600 hover:text-blue-900"
                                                >
                                                    Copy Link
                                                </button>
                                                {!invitation.isUsed && (
                                                    <>
                                                        <button
                                                            onClick={() => handleResendInvitation(invitation.id)}
                                                            className="text-green-600 hover:text-green-900"
                                                        >
                                                            Resend
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteInvitation(invitation.id)}
                                                            className="text-red-600 hover:text-red-900"
                                                        >
                                                            Delete
                                                        </button>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {pagination.pages > 1 && (
                            <div className="mt-4 flex items-center justify-between">
                                <div className="text-sm text-gray-700">
                                    Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                                    {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} invitations
                                </div>
                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => setPagination({...pagination, page: pagination.page - 1})}
                                        disabled={pagination.page === 1}
                                        className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Previous
                                    </button>
                                    <button
                                        onClick={() => setPagination({...pagination, page: pagination.page + 1})}
                                        disabled={pagination.page === pagination.pages}
                                        className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminInvitations;
